import React from 'react';
import PropTypes from 'prop-types';
import { usePaymentInputs } from 'react-payment-inputs';
import { useDispatch, useSelector } from 'react-redux';

import { processCardInfo } from '../../containers/processCardInfo/processCardInfoSlice';
import stateSelectOptions from '../../constants/stateSelectOptions';
import StyledTextButton from '../StyledTextButton';
import OfferDetails from '../../constants/offerDetails';
import TermsConditions from '../../constants/termsAndConditions';
import PrivacyPolicy from '../../constants/privacyPolicy';

import images from 'react-payment-inputs/images';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import LoadingButton from '@mui/lab/LoadingButton';

const PaymentInput = ({ handleNext, uuid }) => {
	const dispatch = useDispatch();
	const {
		getCardNumberProps,
		getExpiryDateProps,
		getCVCProps,
		getCardImageProps
	} = usePaymentInputs();

	const [paymentInformation, setPaymentInformation] = React.useState({
		firstName: '',
		lastName: '',
		cardNumber: '',
		expiryDate: '',
		cvc: '',
		promoCode: '',
		address: '',
		address2: '',
		city: '',
		state: '',
		zip: ''
	});

	const [formErrors, setFormErrors] = React.useState({
		firstName: false,
		lastName: false,
		cardNumber: false,
		expiryDate: false,
		cvc: false
	});
	const [acknowledgmentError, setAcknowledgementError] = React.useState(false);

	const [isSameAddress, setIsSameAddress] = React.useState(false);
	const [addressFieldsVisible, setAddressFieldsVisible] = React.useState(false);
	const [legalDialogOpen, setLegalDialogOpen] = React.useState(false);
	const [content, setContent] = React.useState('');
	const [acknowledged, isAcknowledged] = React.useState({
		readDetails: false,
		agreeToCharge: false
	});

	const { hasCompleted, isLoading, error } = useSelector(
		(state) => state.processCardInfoSlice.processCardInfo
	);

	const contactData = useSelector(
		(state) => state.collectContactInfoSlice.collectContactInfoPayload.data
	);
	React.useEffect(() => {
		if (hasCompleted && !isLoading && !error) {
			handleNext();
		}
	}, [hasCompleted, isLoading, error]);

	const handleInputChange = (event) => {
		const { name, value } = event.target;
		setPaymentInformation({
			...paymentInformation,
			[name]: value
		});
	};

	const handleBlur = (event) => {
		const { name } = event.target;
		if (paymentInformation[name] === '') {
			setFormErrors({
				...formErrors,
				[name]: true
			});
		} else {
			setFormErrors({
				...formErrors,
				[name]: false
			});
		}
	};

	const handleSubmitPayment = () => {
		let valid = true;
		const { readDetails, agreeToCharge } = acknowledged;
		if (!readDetails || !agreeToCharge) {
			setAcknowledgementError(true);
			valid = false;
		} else {
			setAcknowledgementError(false);
		}
		Object.keys(formErrors).forEach((item) => {
			if (!paymentInformation[item].trim()) {
				setFormErrors((prev) => ({
					...prev,
					[item]: true
				}));
				valid = false;
			}
		});
		if (valid) {
			const {
				firstName,
				lastName,
				cardNumber,
				expiryDate,
				cvc,
				promoCode,
				address,
				address2,
				city,
				state,
				zip
			} = paymentInformation;
			const payload = {
				uuid,
				billing_first_name: firstName,
				billing_last_name: lastName,
				card_number: cardNumber.replace(/[ ]/g, ''),
				card_expiration: expiryDate.replace(/[ /]/g, ''),
				card_cvv: cvc,
				promo_code: promoCode,
				billing_address1: !isSameAddress ? contactData?.address1 : address,
				billing_address2: !isSameAddress ? contactData?.address2 : address2,
				billing_city: !isSameAddress ? contactData?.city : city,
				billing_state: !isSameAddress ? contactData?.state : state,
				billing_zip: !isSameAddress ? contactData?.zip : zip,
				same_address: !isSameAddress,
				acknowledged
			};

			dispatch(processCardInfo(payload));
		}
	};

	const handleOpenDialog = (type) => {
		setContent(type);
		setLegalDialogOpen(true);
	};

	const handleCloseDialog = () => {
		setLegalDialogOpen(false);
	};

	const toggleIsSameAddress = () => {
		setIsSameAddress(!isSameAddress);
		setAddressFieldsVisible(!addressFieldsVisible);
	};

	const handleCheck = (event) => {
		isAcknowledged({
			...acknowledged,
			[event.target.name]: event.target.checked
		});
	};

	const dialogContent = () => {
		switch (content) {
			case 'Offer Details':
				return <OfferDetails />;
			case 'Terms and Conditions':
				return <TermsConditions />;
			case 'Privacy Policy':
				return <PrivacyPolicy />;
			default:
				return null;
		}
	};

	return (
		<Stack spacing={2}>
			<Typography variant='h5' fontWeight='bold' component='h3'>
				Credit Card Information
			</Typography>
			<Typography variant='p'>
				Enter your credit card information below
			</Typography>
			{error && (
				<Typography variant='p' fontSize='small' color='error'>
					There was an error processing your payment
				</Typography>
			)}
			<TextField
				id='first-name-input'
				name='firstName'
				label='First Name'
				required
				size='small'
				onChange={handleInputChange}
				onBlur={handleBlur}
				value={paymentInformation.firstName}
				error={formErrors.firstName}
				helperText={formErrors.firstName && 'Enter the first name on the card'}
			/>
			<TextField
				id='last-name-input'
				name='lastName'
				label='Last Name'
				required
				size='small'
				onChange={handleInputChange}
				onBlur={handleBlur}
				value={paymentInformation.lastName}
				error={formErrors.lastName}
				helperText={formErrors.lastName && 'Enter the last name on the card'}
			/>
			<TextField
				id='card-number-input'
				name='cardNumber'
				inputProps={getCardNumberProps({
					onChange: handleInputChange,
					onBlur: handleBlur
				})}
				inputRef={getCardNumberProps().ref}
				value={paymentInformation.cardNumber}
				label='Card Number'
				fullWidth
				required
				size='small'
				error={formErrors.cardNumber}
				helperText={
					(formErrors.cardNumber && 'Enter Card Number') || (
						<Typography variant='span' fontStyle='italic' fontSize='10px'>
							Accepted Credit Cards: Visa, Mastercard, Discover, American
							Express
						</Typography>
					)
				}
				InputProps={{
					endAdornment: (
						<InputAdornment position='end'>
							<svg {...getCardImageProps({ images })} />
						</InputAdornment>
					)
				}}
			/>

			<Stack direction='row' spacing={2}>
				<TextField
					id='card-expiration-input'
					name='expiryDate'
					label='Expiration Month/Year'
					required
					size='small'
					inputRef={getExpiryDateProps().ref}
					inputProps={getExpiryDateProps({
						onChange: handleInputChange,
						onBlur: handleBlur
					})}
					value={paymentInformation.expiryDate}
					error={formErrors.expiryDate}
					helperText={formErrors.expiryDate && 'Enter expiration date'}
				/>
				<TextField
					id='cvc'
					name='cvc'
					label='Security Code'
					required
					size='small'
					inputRef={getCVCProps().ref}
					inputProps={getCVCProps({
						onChange: handleInputChange,
						onBlur: handleBlur
					})}
					value={paymentInformation.cvc}
					error={formErrors.cvc}
					helperText={formErrors.cvc && 'Enter security code'}
				/>
			</Stack>
			<FormControlLabel
				control={
					<Checkbox
						color='default'
						size='small'
						checked={isSameAddress}
						onChange={toggleIsSameAddress}
					/>
				}
				label={
					<Typography fontSize='10px' fontStyle='italic'>
						My billing address is not the same as my account address
					</Typography>
				}
			/>
			{addressFieldsVisible && (
				<>
					<TextField
						onChange={handleInputChange}
						name='address'
						id='address'
						label='Address'
						size='small'
						value={paymentInformation.address}
						fullWidth
					/>
					<TextField
						onChange={handleInputChange}
						name='address2'
						id='address2'
						label='Address 2'
						size='small'
						value={paymentInformation.address2}
						fullWidth
					/>
					<Grid container gap={2}>
						<Grid item md={5}>
							<TextField
								onChange={handleInputChange}
								name='city'
								id='city'
								label='City'
								size='small'
								value={paymentInformation.city}
								fullWidth
								onBlur={handleBlur}
							/>
						</Grid>
						<Grid item md={3}>
							<TextField
								id='payment-info-state'
								name='state'
								label='State'
								fullWidth
								size='small'
								select
								value={paymentInformation.state}
								onBlur={handleBlur}
								onChange={handleInputChange}
							>
								{stateSelectOptions.map((stateItem) => {
									return (
										<MenuItem
											key={stateItem.abbreviation}
											value={stateItem.abbreviation}
										>
											{stateItem.name}
										</MenuItem>
									);
								})}
							</TextField>
						</Grid>
						<Grid item md>
							<TextField
								onChange={handleInputChange}
								name='zip'
								id='zip'
								label='Zip'
								size='small'
								value={paymentInformation.zip}
								fullWidth
								onBlur={handleBlur}
							/>
						</Grid>
					</Grid>
				</>
			)}
			<TextField
				id='promo-code-input'
				name='promoCode'
				label='Apply Promo Code'
				fullWidth
				size='small'
				onChange={handleInputChange}
				onBlur={handleBlur}
				value={paymentInformation.promoCode}
			/>
			<FormControl required error={acknowledgmentError}>
				{acknowledgmentError && (
					<FormLabel component='legend'>
						Must select these to continue
					</FormLabel>
				)}
				<FormControlLabel
					control={
						<Checkbox
							color='default'
							size='small'
							checked={acknowledged.agreeToCharge}
							name='agreeToCharge'
							onClick={handleCheck}
						/>
					}
					label={
						<Typography fontSize='10px' fontStyle='italic' textAlign='left'>
							I have read and agree to the{' '}
							<StyledTextButton
								onClick={() => handleOpenDialog('Offer Details')}
								sx={{
									fontSize: 'inherit',
									color: 'inherit',
									justifyContent: 'center',
									minWidth: 'auto'
								}}
							>
								Offer Details
							</StyledTextButton>
							,{' '}
							<StyledTextButton
								onClick={() => handleOpenDialog('Terms and Conditions')}
								sx={{
									fontSize: 'inherit',
									color: 'inherit',
									justifyContent: 'center',
									minWidth: 'auto'
								}}
							>
								Terms and Conditions
							</StyledTextButton>{' '}
							and{' '}
							<StyledTextButton
								onClick={() => handleOpenDialog('Privacy Policy')}
								sx={{
									fontSize: 'inherit',
									color: 'inherit',
									justifyContent: 'center',
									minWidth: 'auto'
								}}
							>
								Privacy Policy
							</StyledTextButton>{' '}
							and I authorize you to charge my credit/debit card for RizzBates.
						</Typography>
					}
				/>
				<FormControlLabel
					sx={{ verticalAlign: 'flex-start', alignItems: 'flex-start' }}
					control={
						<Checkbox
							color='default'
							size='small'
							checked={acknowledged.readDetails}
							name='readDetails'
							onClick={handleCheck}
						/>
					}
					label={
						<Typography fontSize='10px' fontStyle='italic' textAlign='left'>
							<OfferDetails />
						</Typography>
					}
				/>
			</FormControl>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'center',
					my: 2
				}}
			>
				<LoadingButton
					variant='outlined'
					color='primary'
					onClick={handleSubmitPayment}
					loading={isLoading}
				>
					Submit
				</LoadingButton>
			</Box>
			<Dialog
				fullWidth
				maxWidth='sm'
				onClose={handleCloseDialog}
				aria-labelledby='title'
				open={legalDialogOpen}
			>
				<DialogTitle id='title'>{content}</DialogTitle>
				<DialogContent
					sx={{
						borderRadius: '8px',
						height: '581px',
						maxHeight: '581px',
						overflowY: 'auto',
						fontSize: '15px',
						backgroundColor: (theme) => theme.palette.common.white
					}}
				>
					{dialogContent()}
				</DialogContent>
				<DialogActions sx={{ justifyContent: 'center', my: 2 }}>
					<Button
						size='small'
						sx={{ width: '144px' }}
						onClick={handleCloseDialog}
					>
						Close
					</Button>
				</DialogActions>
			</Dialog>
		</Stack>
	);
};

PaymentInput.propTypes = {
	handleNext: PropTypes.func,
	uuid: PropTypes.string
};

export default PaymentInput;
